// utils
import { lightFormat } from 'date-fns';
import { TransformedBooking } from '../types';
import { MbscDateType } from '@akoom-tech/mobiscroll-react';

// types

export type SchedulerSessionStatusFilter =
  | 'in_progress'
  | 'on_the_way'
  | 'completed'
  | 'upcoming';

export type SchedulerBookingTypeFilter = 'PW' | 'PF' | 'PO' | 'HO';

export type SchedulerIssueFilter = 'arrival_risk' | 'late_start';

const normalizeStatusKey = (value?: string | null) => {
  if (!value) return null;

  const normalized = value
    .trim()
    .replace(/[\s-]+/g, '_')
    .toLowerCase();

  return normalized || null;
};

const IN_PROGRESS_STATUS_KEYS = ['in_progress', 'started'];
const ON_THE_WAY_STATUS_KEYS = ['on_the_way', 'en_route'];
const COMPLETED_STATUS_KEYS = ['completed', 'done', 'finished'];
const UPCOMING_STATUS_KEYS = [
  'not_started',
  'not_yet_started',
  'upcoming',
  'pending',
  'confirmed',
  'new',
  'scheduled',
  'booked',
];
const EXCLUDED_STATUS_KEYS = ['cancelled', 'canceled', 'void', 'error'];

export const resolveSchedulerBookingTypeFilter = (
  session: Pick<TransformedBooking, 'serviceLabel'>
): SchedulerBookingTypeFilter | null => {
  const serviceLabel = session.serviceLabel?.trim().toUpperCase();

  switch (serviceLabel) {
    case 'PW':
    case 'PF':
    case 'PO':
    case 'HO':
      return serviceLabel;
    default:
      return null;
  }
};

export const resolveSchedulerIssueFilters = (
  session: Pick<
    TransformedBooking,
    'arrivalRisk' | 'hasArrivalRiskWarning' | 'startDelayMinutes'
  >
): SchedulerIssueFilter[] => {
  const filters: SchedulerIssueFilter[] = [];
  const hasArrivalRisk =
    session.hasArrivalRiskWarning === true ||
    session.arrivalRisk?.status === 'at_risk' ||
    session.arrivalRisk?.status === 'late';
  const hasLateStart =
    typeof session.startDelayMinutes === 'number' &&
    Number.isFinite(session.startDelayMinutes) &&
    session.startDelayMinutes > 5;

  if (hasArrivalRisk) {
    filters.push('arrival_risk');
  }
  if (hasLateStart) {
    filters.push('late_start');
  }

  return filters;
};

export const resolveSchedulerSessionStatusFilter = (
  session: Pick<
    TransformedBooking,
    'statusDisplay' | 'statusValue' | 'statusStyleClass' | 'cssClass'
  >
): SchedulerSessionStatusFilter | null => {
  const statusKeys = [session.statusValue, session.statusDisplay]
    .map(normalizeStatusKey)
    .filter((value): value is string => Boolean(value));

  if (statusKeys.some((key) => IN_PROGRESS_STATUS_KEYS.includes(key))) {
    return 'in_progress';
  }
  if (statusKeys.some((key) => ON_THE_WAY_STATUS_KEYS.includes(key))) {
    return 'on_the_way';
  }
  if (statusKeys.some((key) => COMPLETED_STATUS_KEYS.includes(key))) {
    return 'completed';
  }
  if (statusKeys.some((key) => UPCOMING_STATUS_KEYS.includes(key))) {
    return 'upcoming';
  }
  if (statusKeys.some((key) => EXCLUDED_STATUS_KEYS.includes(key))) {
    return null;
  }

  const toneKey = normalizeStatusKey(
    session.statusStyleClass ?? session.cssClass
  );

  switch (toneKey) {
    case 'info':
      return 'in_progress';
    case 'warning':
      return 'on_the_way';
    case 'success':
      return 'completed';
    case 'default':
      return 'upcoming';
    default:
      return null;
  }
};

export function applySortFilterCleaningJobs<TCleaner extends { id: number }>({
  bookingSessions,
  cleaners,
  cleanerId, // comparator,
  statusFilters = [],
  bookingTypeFilters = [],
  issueFilters = [],
}: {
  bookingSessions: TransformedBooking[] | undefined;
  cleaners: TCleaner[] | undefined;
  cleanerId: number | null;
  statusFilters?: SchedulerSessionStatusFilter[];
  bookingTypeFilters?: SchedulerBookingTypeFilter[];
  issueFilters?: SchedulerIssueFilter[];
  // comparator: (a: any, b: any) => number;
}): [TransformedBooking[], TCleaner[]] | undefined {
  if (!bookingSessions || !cleaners) return undefined;
  // tableData = tableSortFilter(tableData, comparator);

  if (cleanerId) {
    bookingSessions = bookingSessions.filter(
      (session) => session.resource === cleanerId
    );
    cleaners = cleaners.filter((cleaner) => cleaner.id === cleanerId);
  }

  if (statusFilters.length > 0) {
    const selectedStatuses = new Set(statusFilters);

    bookingSessions = bookingSessions.filter((session) => {
      const statusFilter = resolveSchedulerSessionStatusFilter(session);

      return statusFilter ? selectedStatuses.has(statusFilter) : false;
    });
  }

  if (bookingTypeFilters.length > 0) {
    const selectedBookingTypes = new Set(bookingTypeFilters);

    bookingSessions = bookingSessions.filter((session) => {
      const bookingTypeFilter = resolveSchedulerBookingTypeFilter(session);

      return bookingTypeFilter
        ? selectedBookingTypes.has(bookingTypeFilter)
        : false;
    });
  }

  if (issueFilters.length > 0) {
    const selectedIssues = new Set(issueFilters);

    bookingSessions = bookingSessions.filter((session) =>
      resolveSchedulerIssueFilters(session).some((issueFilter) =>
        selectedIssues.has(issueFilter)
      )
    );
  }

  return [bookingSessions, cleaners];
}

export const getNextPrevDay = (date: Date, param: string): string => {
  const dayToReturn = new Date(date);
  const number = param === 'next' ? 1 : -1;
  dayToReturn.setDate(date.getDate() + number);
  return lightFormat(new Date(dayToReturn), 'yyyy-MM-dd');
};

export const formatMbscDateType = (mbscDate: MbscDateType): Date => {
  if (mbscDate instanceof Date) {
    return mbscDate;
  }
  if (typeof mbscDate === 'string' || typeof mbscDate === 'number') {
    return new Date(mbscDate);
  }
  throw new Error('Invalid MbscDateType');
};
